/* eslint-disable import/no-cycle */
import React, { useState, useMemo, useCallback } from 'react';
import { Button, makeStyles } from '@material-ui/core';
import { LazyLoadingComponent } from '@/../LazyLoading';
import config from '@/configs/config';
import { map, sortBy } from 'lodash';
import moment from 'moment';
import MenuProviderModal from './MenuProviderModal';

const useItemStyle = makeStyles((theme) => ({
  root: {
    alignItems: 'center',
    color: 'black',
    display: 'flex',
    fontSize: 16,
    fontWeight: 'bold',
    overflow: 'hidden',
    position: 'relative',

    '&+&': { marginTop: theme.spacing(0.5) },
  },
  sign: {
    borderRadius: '50%',
    height: 8,
    marginRight: theme.spacing(1),
    width: 8,

    '&.pre-order': { background: theme.palette.inventoryStatus.preOrder },
    '&.high': { background: theme.palette.inventoryStatus.high },
    '&.low': { background: theme.palette.inventoryStatus.low },
    '&.empty': { background: theme.palette.inventoryStatus.empty },
  },
  name: {
    background: 'transparent',
    border: 0,
    color: '#000000',
    cursor: 'pointer',
    flexShrink: 1,
    fontSize: 14,
    lineHeight: 1,
    outline: 'none',
    overflow: 'hidden',
    padding: 0,
    textAlign: 'left',
    textDecoration: 'none',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  price: {
    flexShrink: 0,
    fontSize: ({ isOnSale }) => (isOnSale ? 12 : 14),
    marginLeft: theme.spacing(1),
    textDecoration: ({ isOnSale }) => (isOnSale ? 'line-through' : 'none'),
    fontFamily: 'Roboto',
  },
  sellPrice: {
    color: theme.palette.price.onSale,
    flexShrink: 0,
    fontSize: 14,
    marginLeft: theme.spacing(1),
    fontFamily: 'Roboto',
  },
  gap: {
    flexGrow: 1,
    flexShrink: 1,
  },
  inventoryContainer: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    marginLeft: theme.spacing(1),
  },
  redeemText: {
    color: 'rgb(148,139,139)',
    fontSize: 13,
    marginLeft: theme.spacing(1),
  },
}));

const InventoryStringMap = {
  0: '銷售一空',
  1: '即將完售',
  2: '庫存充足',
  3: '預訂',
};

const InventoryClassNameMap = {
  0: 'empty',
  1: 'low',
  2: 'high',
  3: 'pre-order',
};

const useInventoryStatusLabelStyle = makeStyles((theme) => ({
  root: {
    alignItems: 'center',
    background: '#a8a6a6',
    borderRadius: 5,
    color: 'white',
    display: 'flex',
    fontSize: 12,
    fontWeight: 'bold',
    padding: theme.spacing(0, 1),
    border: 0,
    outline: 0,

    '&.pre-order': { background: theme.palette.inventoryStatus.preOrder },
    '&.high': { background: theme.palette.inventoryStatus.high },
    '&.low': { background: theme.palette.inventoryStatus.low },
    '&.empty': { background: theme.palette.inventoryStatus.empty },
  },
}));

export const InverntoryStatusLabel = ({
  status,
  onClick,
}) => {
  const classes = useInventoryStatusLabelStyle();
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        classes.root,
        InventoryClassNameMap[status] || '',
      ].join(' ')}
    >
      {InventoryStringMap[status]}
    </button>
  );
};

const Item = ({
  data,
  id,
  inventory,
  name,
  onClick: propsOnClick,
  price,
  sellPrice,
  showInventoryStatusSign,
}) => {
  const classes = useItemStyle({ isOnSale: (sellPrice || price) < price });
  const { redeemStartTime } = data || {};
  const onClick = () => {
    propsOnClick({ ...data, id });
  };

  return (
    <div className={classes.root}>
      {showInventoryStatusSign
      && <div className={[classes.sign, InventoryClassNameMap[inventory || 0]].join(' ')} />}
      <button type="button" className={classes.name} onClick={onClick}>{name}</button>
      <div className={classes.gap} />
      <div className={classes.price}>{`$ ${price}`}</div>
      {(sellPrice || price) < price
      && <div className={classes.sellPrice}>{`$ ${sellPrice}`}</div>}
      {inventory === 3 && (
      <div className={classes.inventoryContainer}>
        <InverntoryStatusLabel status={inventory} onClick={onClick} />
        <span className={classes.redeemText}>{`${moment(redeemStartTime).utcOffset(8).format('MM/DD')}到`}</span>
      </div>
      )}
    </div>
  );
};

const useProductStyle = makeStyles((theme) => ({
  container: {
    display: 'inline-block',
    overflow: 'visible',
    width: '100%',
  },
  root: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  name: {
    color: 'black',
    fontSize: 14,
    fontWeight: 'bold',
  },
  intro: {
    color: '#a8a6a6',
    fontSize: 12,
  },
  image: {
    borderRadius: 8,
    overflow: 'hidden',
    paddingTop: '50%',
    position: 'relative',
  },
  innerImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
  },
  campaign: {
    alignItems: 'center',
    background: '#a8a6a6',
    border: 0,
    borderRadius: 5,
    color: 'white',
    display: 'flex',
    fontSize: 13,
    fontWeight: 'bold',
    outline: 0,
    padding: theme.spacing(0, 1.5),

    '& + &': {
      marginTop: theme.spacing(0.5),
    },
  },
  products: { marginTop: theme.spacing(1) },
  campaignsContainer: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1, 0),
    width: '100%',
  },
  displayMoreButton: {
    display: 'inline-block',
    alignSelf: 'center',
    width: '96px',
    borderRadius: '4px',
    marginTop: theme.spacing(1.5),
    boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.1)',
  },
}));

export default ({
  images,
  introduction,
  onProductClick,
  products,
  providerName,
  showInventoryStatusSign,
  isOpenOnNewTab = false,
}) => {
  const classes = useProductStyle();
  const [isModalOpend, setIsModalOpend] = useState(false);
  const onClick = () => {
    if (!isOpenOnNewTab) return;

    setIsModalOpend(true);
  };
  const closeModal = () => {
    setIsModalOpend(false);
  };

  const [isHideOverProduct, setIsHideOverProduct] = useState(true);

  const toggleDisplayMoreText = isHideOverProduct ? '顯示更多' : '顯示較少';

  const onToggleDisplayMore = useCallback(
    () => setIsHideOverProduct((prevStatus) => !prevStatus),
    []
  );

  const countOfProducts = products.length;

  const sortedProducts = useMemo(() => sortBy(products, 'name'), [products]);

  const canDisplayProducts = useMemo(
    () => (isHideOverProduct ? sortedProducts.slice(0, 5) : sortedProducts),
    [isHideOverProduct, sortedProducts]
  );

  return (
    <>
      <MenuProviderModal
        isOpen={isModalOpend}
        onClose={closeModal}
        data={{
          images,
          introduction,
          onProductClick,
          products,
          providerName,
          showInventoryStatusSign,
        }}
      />
      <div className={classes.container} onClick={onClick}>
        <div className={classes.root}>
          <div className={classes.image}>
            <LazyLoadingComponent>
              {
              images.split('+').map((url) => (
                <img
                  src={`${config.RESOURCE_URI}${url}`}
                  alt=""
                  className={classes.innerImage}
                  key={url}
                  onError={(e) => {
                    e.currentTarget.src = `${config.RESOURCE_URI}provider_default.png`;
                    e.currentTarget.width = '100%';
                  }}
                />
              ))
            }
            </LazyLoadingComponent>
          </div>
          <div className={classes.name}>{providerName}</div>
          <div className={classes.intro}>{introduction}</div>
          {isOpenOnNewTab === false
        && (
        <div className={classes.products}>
          {
            map(canDisplayProducts, (product) => {
              const {
                inventory, name, price, id, sellPrice,
              } = product || {};
              return (
                <Item
                  data={product}
                  id={id}
                  inventory={inventory}
                  key={id}
                  name={name}
                  onClick={onProductClick}
                  price={price}
                  sellPrice={sellPrice}
                  showInventoryStatusSign={showInventoryStatusSign}
                />
              );
            })
          }
        </div>
        )}
          {
            countOfProducts > 5 && (
              <Button
                className={classes.displayMoreButton}
                onClick={onToggleDisplayMore}
              >
                {toggleDisplayMoreText}
              </Button>
            )
          }
        </div>
      </div>
    </>
  );
};
